import { Button, Flex, Layout } from 'antd'
import { isEmpty } from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import CustomInput from 'src/app/shared/components/common/input'
import CustomSelect from 'src/app/shared/components/common/select'
import { ActivationWarning } from 'src/app/shared/components/common/tag/ActivationWarning'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'
import { inputNumber } from 'src/app/shared/utils'
import { getLookupValues } from 'src/app/store/masterSetup'
import ACHForm from '../components/ACHForm'
import CheckForm from '../components/CheckForm'
import CustomConfirmationModal from '../components/CustomConfirmationModal'
import PayeeMatchWarningModal from '../components/PayeeMatchWarningModal'
import '../styles/index.scss'
import { useFormRules } from '../utils'
import { useNewAccountContext } from './index.context'

const NewAccountContent: React.FC = () => {
  const { t } = useTranslation()
  const rules = useFormRules()
  const lookupValues = useSelector(getLookupValues)
  const {
    client,
    form,
    achExceptionProcessingType,
    checkExceptionProcessingType,
    loading,
    clientList,
    modalRef,
    payeeMatchWarningRef,
    onSubmit,
    activationDateWarning,
  } = useNewAccountContext()
  const { handleSubmit, control, formState } = useMemo(() => form, [form])
  const { errors, isValid, isDirty } = useMemo(() => formState, [formState])
  const navigate = useNavigate()
  const { mode } = useWindowSize()

  const accountNumber = useWatch({
    control,
    name: 'accountNumber',
  })

  const currentChargeNumber = useWatch({
    control,
    name: 'chargeAccountNumber',
  })

  const achExceptionType = useWatch({
    control,
    name: 'achExceptionProcessingType',
  })
  const checkExceptionType = useWatch({
    control,
    name: 'checkExceptionProcessingType',
  })

  useEffect(() => {
    const bothNoCreate = achExceptionType === 'NOCREATE' && checkExceptionType === 'NOCREATE'

    if (bothNoCreate) {
      form.setError('achExceptionProcessingType', {
        type: 'manual',
        message: 'At least one exception type (ACH or Check) must be enabled to proceed.',
      })
      form.setError('checkExceptionProcessingType', {
        type: 'manual',
        message: 'At least one exception type (ACH or Check) must be enabled to proceed.',
      })
    } else {
      form.clearErrors(['achExceptionProcessingType', 'checkExceptionProcessingType'])
    }
  }, [achExceptionType, checkExceptionType, form])

  return (
    <Layout.Content style={{ padding: '24px 24px 0px 24px' }}>
      {activationDateWarning && <ActivationWarning activationDate={activationDateWarning} />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex
          style={{
            flexDirection: 'column',
            gap: 24,
          }}
        >
          <Controller
            control={control}
            name={'clientName'}
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('AccountSetup:new_account_form.title.clientName')}
                placeholder={t('AccountSetup:new_account_form.placeholder.clientName')}
                required
                value={!field.value ? null : field.value}
                onChange={field.onChange}
                onBlur={async (event) => {
                  field.onBlur()
                  await form.trigger('clientName')
                }}
                options={clientList.map((item) => ({
                  label: item.clientName,
                  value: item.id,
                }))}
                error={fieldState.error}
                loading={loading}
                showSearch
              />
            )}
            rules={rules.clientName}
          />
          <Flex
            style={{
              gap: 24,
              flexDirection: mode === 'mobile' ? 'column' : undefined,
            }}
          >
            <Controller
              control={control}
              name={'accountName'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.accountName')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.accountName')}
                  required
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={async (event) => {
                    field.onBlur()
                    field.onChange(event?.target?.value?.trim())
                    await form.trigger('accountName')
                  }}
                  error={fieldState.error}
                  maxLength={(rules.accountName.maxLength as any).value}
                />
              )}
              rules={rules.accountName}
            />
            <Controller
              control={control}
              name={'accountNumber'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.accountNumber')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.accountNumber')}
                  required
                  value={field.value}
                  onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                  onBlur={async () => {
                    field.onBlur()
                    if (!currentChargeNumber) {
                      form.setValue('chargeAccountNumber', accountNumber)
                      form.clearErrors('chargeAccountNumber')
                    }
                    await form.trigger('accountNumber')
                  }}
                  error={fieldState.error}
                  maxLength={(rules.accountNumber.maxLength as any).value}
                />
              )}
              rules={rules.accountNumber}
            />
          </Flex>
          <Flex
            style={{
              gap: 24,
              flexDirection: mode === 'mobile' ? 'column' : undefined,
            }}
          >
            <Controller
              control={control}
              name={'chargeAccountNumber'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.chargeAccountNumber')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.chargeAccountNumber')}
                  value={field.value}
                  onChange={(e) =>
                    field.onChange(inputNumber(e, (rules.chargeAccountNumber.maxLength as any).value), {
                      shouldValidate: true,
                    })
                  }
                  onBlur={async () => {
                    field.onBlur()
                    await form.trigger('chargeAccountNumber')
                  }}
                  error={fieldState.error}
                  maxLength={(rules.chargeAccountNumber.maxLength as any).value}
                  required
                />
              )}
              rules={rules.chargeAccountNumber}
            />
            <Controller
              control={control}
              name={'displayNotes'}
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('AccountSetup:new_account_form.title.displayNotes')}
                  placeholder={t('AccountSetup:new_account_form.placeholder.displayNotes')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error}
                  maxLength={(rules.displayNotes.maxLength as any).value}
                  disabled
                />
              )}
              rules={rules.displayNotes}
            />
          </Flex>

          <Controller
            control={control}
            name={'serviceTypeParameter'}
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('AccountSetup:new_account_form.title.serviceTypeParameter')}
                placeholder={t('AccountSetup:new_account_form.placeholder.serviceTypeParameter')}
                value={!field.value ? null : field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error}
                options={lookupValues?.SrvChg}
                maxLength={(rules.serviceTypeParameter.maxLength as any).value}
                disabled
                loading={loading}
              />
            )}
            rules={rules.serviceTypeParameter}
          />

          <CheckForm
            checkExceptionProcessingType={checkExceptionProcessingType}
            control={control}
            form={form}
            loading={loading}
            client={client}
            isAddAccount
          />

          <ACHForm
            achExceptionProcessingType={achExceptionProcessingType}
            control={control}
            form={form}
            loading={loading}
            client={client}
          />

          <Flex
            style={{
              justifyContent: 'flex-end',
              gap: 24,
            }}
            className='button-group-container'
          >
            <Button
              type='default'
              className='new-account-cancel-button'
              onClick={() =>
                navigate(menuUrls.accountSetup, {
                  replace: true,
                })
              }
              disabled={loading}
            >
              {t('common:cancel')}
            </Button>
            <Button
              type='primary'
              className='new-account-button'
              htmlType='button'
              disabled={!isValid || !isEmpty(errors) || !isDirty || loading}
              onClick={handleSubmit(onSubmit)}
              loading={loading}
            >
              {t('AccountSetup:new_account_form.title.saveChanges')}
            </Button>
          </Flex>
        </Flex>
      </form>
      <CustomConfirmationModal ref={modalRef} />
      <PayeeMatchWarningModal ref={payeeMatchWarningRef} />
    </Layout.Content>
  )
}

export default NewAccountContent
